import { cn } from '@/lib/classNames';
import { CheckMark } from '@/resources/assets/images';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';

const PopoverRoot = PopoverPrimitive.Root;

const PopoverPortal = PopoverPrimitive.Portal;

const PopoverTrigger = forwardRef<
	ElementRef<typeof PopoverPrimitive.Trigger>,
	ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
	<PopoverPrimitive.Trigger
		ref={ref}
		className={cn(
			'flex h-9 w-full cursor-pointer items-center justify-between whitespace-nowrap rounded-md border border-dm-gray bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-transparent placeholder:text-lightest-grey focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
			className
		)}
		{...props}
	>
		{children}
	</PopoverPrimitive.Trigger>
));

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = forwardRef<
	ElementRef<typeof PopoverPrimitive.Content>,
	ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
	<PopoverPrimitive.Content
		ref={ref}
		align={align}
		sideOffset={sideOffset}
		className={cn(
			'z-50 w-72 rounded-md border bg-dm-black p-4 text-lightest-grey shadow-md outline-none',
			'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
			'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
			'data-[side=bottom]:slide-in-from-top-2',
			'data-[side=left]:slide-in-from-right-2',
			'data-[side=right]:slide-in-from-left-2',
			'data-[side=top]:slide-in-from-bottom-2',
			className
		)}
		{...props}
	/>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

type PopoverButtonProps = ComponentPropsWithoutRef<'button'> & {
	isSelected?: boolean;
};

const PopoverButton = forwardRef<ElementRef<'button'>, PopoverButtonProps>(
	({ isSelected, className, children, ...props }, ref) => {
		return (
			<button
				type="button"
				ref={ref}
				data-is-selected={isSelected}
				className={cn(
					'inline-flex h-10 w-full items-center justify-between overflow-hidden text-ellipsis whitespace-nowrap rounded-md px-3 text-start font-barlow text-sm transition-colors hover:bg-dm-black3.5',
					'[&:not(:hover)]:data-[is-selected=true]:bg-dm-black3',
					className
				)}
				{...props}
			>
				{children}
				{isSelected && <CheckMark />}
			</button>
		);
	}
);
PopoverButton.displayName = 'PopoverButton';

export {
	PopoverRoot,
	PopoverPortal,
	PopoverTrigger,
	PopoverContent,
	PopoverAnchor,
	PopoverButton
};
